import {Nullable} from "./caos-def";
import {HashParameters, HashParameterValue, MutableHashParameters} from "./navigation";
import {getValue} from "./utils.objects";

// noinspection JSUnusedGlobalSymbols
export function setHash(parameters: string | MutableHashParameters, replace?: boolean) {
    let oldHash = window.location.hash ?? "";
    if (oldHash && oldHash[0] === "#") {
        oldHash = oldHash.substring(1);
    }
    let newHash = calculateUrlHash(parameters);
    if (oldHash === newHash) {
        return;
    }
    if (replace === true) {
        let url = window.location.href.split("#")[0];
        if (newHash.length > 0 && newHash[0] !== "#") {
            newHash = "#" + newHash;
        }
        url += newHash;
        window.history.replaceState(null, "", url);
    } else {
        window.location.hash = newHash;
    }
}

export function calculateUrlHash(parameters: string | MutableHashParameters): string {
    if (typeof parameters === "string") {
        window.location.hash = parameters;
        return "";
    }

    let hash = "";

    for (const key in parameters) {
        const value = parameters[key];
        if (Array.isArray(value)) {
            for (const arrayItem of value) {
                hash += "&" + key + "[]=" + encodeURIComponent(arrayItem);
            }
        } else if (typeof value === "object") {
            for (const objectKey in value) {
                hash += "&" + key + "[" + objectKey + "]=" + encodeURIComponent(value[objectKey]);
            }
        } else {
            hash += "&" + key + "=" + encodeURIComponent(value);
        }
    }

    if (hash) {
        hash = hash.substring(1);
    }
    return hash.trim();
}


/**
 * Gets the initial variant from this URL.
 * searching in order: hash, query parameters, and finally, last path component
 * @param key
 * @param hashParameters
 */
export function getUrlParameter(
    key: string,
    ...hashParameters: HashParameters[]
): Nullable<HashParameterValue> {

    // Get hash and query parameters if not provided
    if (hashParameters.length === 0) {
        hashParameters = [
            getHashParameters(),
            getUrlQueryParameters()
        ];
    }

    // Find and return first parameter
    for (const parameters of hashParameters) {
        const value: Nullable<HashParameterValue> = getValue(parameters, key);
        if (value != null) {
            return value;
        }
    }

    return null;
}

export function getHashParameters(hash?: string): HashParameters {
    return _parseForParameters(hash ?? window.location.hash);
}

export function getUrlQueryParameters(): HashParameters {
    return _parseForParameters(window.location.search ?? "");
}


const parametersCache:{[url:string]: HashParameters} = {};

function _parseForParameters(parametersString: string): HashParameters {
    parametersString = parametersString.trimStart();
    if (parametersString.length == 0 || parametersString === '#' || parametersString === '?') {
        return {};
    }
    if (parametersString[0] === "#") {
        parametersString = "?" + parametersString.substring(1);
    } else if (parametersString[0] !== "?") {
        parametersString = "?" + parametersString;
    }
    if (typeof parametersCache[parametersString] !== "undefined") {
        return parametersCache[parametersString];
    }
    const params: { [key: string]: string | string[] | { [key: string]: string } } = {};
    const urlParameters = new URLSearchParams(parametersString);

    for (const [d, e] of urlParameters) {
        const key: string = decodeURIComponent(d);
        const value: string = decodeURIComponent(e);
        if (key.endsWith("[]")) {
            pushArrayParameter(params, key, value);
            continue;
        }
        const objectIndex: Nullable<string> = key.match(/\[([a-z0-9_/\s,.-])+]$/g)?.groups?.[1];
        if (objectIndex) {
            pushParameterWithIndex(params, key, objectIndex, value);
        } else {
            params[key] = value;
        }
    }
    // parametersCache[parametersString] = params;
    return params
}

function pushArrayParameter(params: HashParameters, key: string, value: string): void {
    key = key.replace("[]", "");
    if (!Array.isArray(params)) {
        if (params[key]) {
            // Move existing value into an array value
            params[key] = [params[key] as string, value];
        } else {
            // Creat an array value with new value
            params[key] = [value];
        }
    } else {
        // push new value to end of array
        (<string[]>params[key]).push(value)
    }
}

function pushParameterWithIndex(params: HashParameters, key: string, index: string, value: string): void {
    key = key.replace("[" + index + "]", "");
    if (!params[key]) {
        params[key] = {};
    }
    params[index] = value;
}